'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function SalaryCalculator() {
  const t = useTranslations("tools.salaryCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    salaryAmount: 50,
    payFrequency: 'hourly',
    hoursPerWeek: 40,
    daysPerWeek: 5,
    holidays: 10,
    vacationDays: 15
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2, margin: 0 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, margin: '1rem 0 0 0' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { maxWidth: '100%', margin: '0 auto' },
      {},
      { maxWidth: '700px' },
      { maxWidth: '900px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, margin: '0 0 0.5rem 0' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary, margin: 0 },
      { fontSize: '0.9rem' },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' }
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    formGroup: getResponsiveStyle(
      { marginBottom: '1.5rem' },
      {},
      {},
      {}
    ),
    formRow: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: '1fr 1fr' },
      { gridTemplateColumns: '1fr 1fr' }
    ),
    label: getResponsiveStyle(
      { display: 'block', marginBottom: '0.5rem', color: '#334155', fontWeight: 600 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      outline: 'none',
    },
    select: {
      width: '100%',
      padding: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      outline: 'none',
      background: 'white',
    },
    inputWithUnit: getResponsiveStyle(
      { display: 'flex', gap: '0.5rem', alignItems: 'center' },
      { flexDirection: 'column' as const, alignItems: 'stretch' as const },
      { flexDirection: 'row' as const, alignItems: 'center' as const },
      { flexDirection: 'row' as const, alignItems: 'center' as const }
    ),
    inputFlex: {
      flex: 1,
    },
    selectFlex: getResponsiveStyle(
      {},
      { flex: 1 },
      { flex: '0 0 140px' },
      { flex: '0 0 140px' }
    ),
    calculateButton: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.5rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        marginTop: '1rem'
      },
      { padding: '1rem', fontSize: '1rem' },
      { padding: '1rem', fontSize: '1.1rem' },
      { padding: '1rem', fontSize: '1.1rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: colors.textPrimary, margin: '0 0 1.5rem 0', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.5rem' }
    ),
    resultsTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      marginBottom: '1.5rem'
    },
    tableHeader: {
      background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      padding: '1rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#334155',
      borderBottom: '2px solid #cbd5e1'
    },
    tableHeaderRight: {
      textAlign: 'right' as const
    },
    tableCell: getResponsiveStyle(
      { padding: '1rem', borderBottom: '1px solid #e2e8f0', color: '#475569' },
      { padding: '0.75rem 0.5rem' },
      { padding: '1rem' },
      { padding: '1rem' }
    ),
    tableCellBold: {
      fontWeight: 600,
      color: colors.textPrimary
    },
    tableCellRight: {
      textAlign: 'right' as const,
      fontWeight: 600,
      color: '#2563eb'
    },
    highlightRow: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
    },
    highlightCell: {
      fontSize: '1.1rem',
      color: '#92400e'
    },
    infoNote: getResponsiveStyle(
      {
        background: '#f0f9ff',
        borderLeft: '4px solid #2563eb',
        padding: '1rem',
        borderRadius: '0.5rem',
        color: '#1e40af',
        marginTop: '1.5rem'
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.9rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      { padding: '2.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    list: getResponsiveStyle(
      { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    listItem: {
      marginBottom: '0.75rem'
    },
    strong: {
      color: colors.textPrimary,
      fontWeight: 600
    },
    faqItem: {
      marginBottom: '1.5rem'
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.125rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
      transition: 'color 0.2s ease'
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyDecimal = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const calculateSalaries = (
    amount: number,
    frequency: string,
    hoursPerWeek: number,
    daysPerWeek: number,
    holidays: number,
    vacationDays: number
  ) => {
    const weeksPerYear = 52;
    const workDaysPerYear = weeksPerYear * daysPerWeek;
    const adjustedWorkDays = workDaysPerYear - holidays - vacationDays;

    let hourlyRate: number;

    switch(frequency) {
      case 'hourly':
        hourlyRate = amount;
        break;
      case 'daily':
        hourlyRate = amount / (hoursPerWeek / daysPerWeek);
        break;
      case 'weekly':
        hourlyRate = amount / hoursPerWeek;
        break;
      case 'biweekly':
        hourlyRate = amount / (hoursPerWeek * 2);
        break;
      case 'semimonthly':
        hourlyRate = amount / (hoursPerWeek * 52 / 24);
        break;
      case 'monthly':
        hourlyRate = amount / (hoursPerWeek * 52 / 12);
        break;
      case 'quarterly':
        hourlyRate = amount / (hoursPerWeek * 52 / 4);
        break;
      case 'annual':
        hourlyRate = amount / (hoursPerWeek * 52);
        break;
      default:
        hourlyRate = amount;
    }

    const hoursPerDay = hoursPerWeek / daysPerWeek;
    const adjustedHourlyRate = hourlyRate * (workDaysPerYear / adjustedWorkDays);

    return {
      hourly: {
        unadjusted: hourlyRate,
        adjusted: adjustedHourlyRate
      },
      daily: {
        unadjusted: hourlyRate * hoursPerDay,
        adjusted: adjustedHourlyRate * hoursPerDay
      },
      weekly: {
        unadjusted: hourlyRate * hoursPerWeek,
        adjusted: adjustedHourlyRate * hoursPerWeek
      },
      biweekly: {
        unadjusted: hourlyRate * hoursPerWeek * 2,
        adjusted: adjustedHourlyRate * hoursPerWeek * 2
      },
      semimonthly: {
        unadjusted: hourlyRate * hoursPerWeek * 52 / 24,
        adjusted: adjustedHourlyRate * hoursPerWeek * 52 / 24
      },
      monthly: {
        unadjusted: hourlyRate * hoursPerWeek * 52 / 12,
        adjusted: adjustedHourlyRate * hoursPerWeek * 52 / 12
      },
      quarterly: {
        unadjusted: hourlyRate * hoursPerWeek * 52 / 4,
        adjusted: adjustedHourlyRate * hoursPerWeek * 52 / 4
      },
      annual: {
        unadjusted: hourlyRate * hoursPerWeek * 52,
        adjusted: adjustedHourlyRate * hoursPerWeek * 52
      }
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (inputs.salaryAmount <= 0 || inputs.hoursPerWeek <= 0 || inputs.daysPerWeek <= 0) {
      alert(t('validationError'));
      return;
    }

    const calculatedResults = calculateSalaries(
      inputs.salaryAmount,
      inputs.payFrequency,
      inputs.hoursPerWeek,
      inputs.daysPerWeek,
      inputs.holidays,
      inputs.vacationDays
    );

    setResults(calculatedResults);

    setTimeout(() => {
      const resultsElement = document.getElementById('results');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const periods = [
    { name: t('periodHourly'), key: 'hourly', useDecimal: true },
    { name: t('periodDaily'), key: 'daily', useDecimal: true },
    { name: t('periodWeekly'), key: 'weekly', useDecimal: false },
    { name: t('periodBiweekly'), key: 'biweekly', useDecimal: false },
    { name: t('periodSemimonthly'), key: 'semimonthly', useDecimal: false },
    { name: t('periodMonthly'), key: 'monthly', useDecimal: false },
    { name: t('periodQuarterly'), key: 'quarterly', useDecimal: false },
    { name: t('periodAnnual'), key: 'annual', useDecimal: false }
  ];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
            <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            <div style={styles.formGroup}>
              <label htmlFor="salary-amount" style={styles.label}>{t('salaryAmountLabel')}</label>
              <div style={styles.inputWithUnit}>
                <input
                  type="number"
                  id="salary-amount"
                  step="0.01"
                  min="0"
                  value={inputs.salaryAmount}
                  onChange={(e) => setInputs({...inputs, salaryAmount: parseFloat(e.target.value) || 0})}
                  style={{...styles.input, ...styles.inputFlex}}
                  required
                />
                <select
                  id="pay-frequency"
                  value={inputs.payFrequency}
                  onChange={(e) => setInputs({...inputs, payFrequency: e.target.value})}
                  style={{...styles.select, ...styles.selectFlex}}
                >
                  <option value="hourly">{t('perHour')}</option>
                  <option value="daily">{t('perDay')}</option>
                  <option value="weekly">{t('perWeek')}</option>
                  <option value="biweekly">{t('perBiweek')}</option>
                  <option value="semimonthly">{t('perSemimonth')}</option>
                  <option value="monthly">{t('perMonth')}</option>
                  <option value="quarterly">{t('perQuarter')}</option>
                  <option value="annual">{t('perYear')}</option>
                </select>
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label htmlFor="hours-per-week" style={styles.label}>{t('hoursPerWeekLabel')}</label>
                <input
                  type="number"
                  id="hours-per-week"
                  min="1"
                  max="168"
                  value={inputs.hoursPerWeek}
                  onChange={(e) => setInputs({...inputs, hoursPerWeek: parseFloat(e.target.value) || 0})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="days-per-week" style={styles.label}>{t('daysPerWeekLabel')}</label>
                <input
                  type="number"
                  id="days-per-week"
                  min="1"
                  max="7"
                  value={inputs.daysPerWeek}
                  onChange={(e) => setInputs({...inputs, daysPerWeek: parseFloat(e.target.value) || 0})}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <div style={styles.formRow}>
              <div style={styles.formGroup}>
                <label htmlFor="holidays" style={styles.label}>{t('holidaysLabel')}</label>
                <input
                  type="number"
                  id="holidays"
                  min="0"
                  max="365"
                  value={inputs.holidays}
                  onChange={(e) => setInputs({...inputs, holidays: parseFloat(e.target.value) || 0})}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label htmlFor="vacation-days" style={styles.label}>{t('vacationDaysLabel')}</label>
                <input
                  type="number"
                  id="vacation-days"
                  min="0"
                  max="365"
                  value={inputs.vacationDays}
                  onChange={(e) => setInputs({...inputs, vacationDays: parseFloat(e.target.value) || 0})}
                  style={styles.input}
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              style={styles.calculateButton}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 20px -10px rgba(37, 99, 235, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={styles.results} id="results">
              <h3 style={styles.resultsTitle}>{t('resultsTitle')}</h3>
              <table style={styles.resultsTable}>
                <thead>
                  <tr>
                    <th style={{...styles.tableHeader, borderRadius: '0.5rem 0 0 0'}}>{t('tableHeaderPeriod')}</th>
                    <th style={styles.tableHeader}>{t('tableHeaderUnadjusted')}</th>
                    <th style={{...styles.tableHeader, ...styles.tableHeaderRight, borderRadius: '0 0.5rem 0 0'}}>{t('tableHeaderAdjusted')}</th>
                  </tr>
                </thead>
                <tbody>
                  {periods.map((period) => {
                    const isHighlight = period.key === inputs.payFrequency;
                    const formatter = period.useDecimal ? formatCurrencyDecimal : formatCurrency;

                    return (
                      <tr
                        key={period.key}
                        style={isHighlight ? styles.highlightRow : {}}
                        onMouseEnter={(e) => {
                          if (!isHighlight) {
                            e.currentTarget.style.background = '#f8fafc';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isHighlight) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <td style={{...styles.tableCell, ...styles.tableCellBold, ...(isHighlight ? styles.highlightCell : {})}}>
                          {period.name}
                        </td>
                        <td style={{...styles.tableCell, ...(isHighlight ? styles.highlightCell : {})}}>
                          {formatter(results[period.key].unadjusted)}
                        </td>
                        <td style={{...styles.tableCell, ...styles.tableCellRight, ...(isHighlight ? styles.highlightCell : {})}}>
                          {formatter(results[period.key].adjusted)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={styles.infoNote}>
                💡 <strong>{t('noteTitle')}</strong> {t('noteText')}
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t('step1Title')}</span> {t('step1Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step2Title')}</span> {t('step2Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step3Title')}</span> {t('step3Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step4Title')}</span> {t('step4Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('step5Title')}</span> {t('step5Text')}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight1Title')}</span> {t('insight1Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight2Title')}</span> {t('insight2Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight3Title')}</span> {t('insight3Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight4Title')}</span> {t('insight4Text')}</li>
          <li style={styles.listItem}><span style={styles.strong}>{t('insight5Title')}</span> {t('insight5Text')}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph3')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph4')}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq1Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq1Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq2Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq2Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq3Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq3Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq4Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq4Answer')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faq5Question')}</h3>
          <p style={styles.faqAnswer}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a
              href="https://www.adp.com/resources/tools/calculators.aspx"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              ADP – Salary, Payroll, 401k, Tax and Health & Benefits Calculators
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://quickbooks.intuit.com/payroll/paycheck-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              QuickBooks – Free Paycheck Calculator (Hourly & Salary)
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://tgcsearch.com/business-insights/paycheck-salary-calculator/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              TGC Search – 2025 Paycheck Salary Calculator and Guide
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://www.adp.com/resources/tools/calculators/salary-paycheck-calculator.aspx"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              ADP – Salary Paycheck Calculator: Calculate Net Income
            </a>
          </li>
          <li style={styles.listItem}>
            <a
              href="https://onpay.com/payroll/calculator-tax-rates/"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.referenceLink}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              OnPay – Free Employer Payroll Calculator and 2025 Tax Rates
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}